import {
  Box,
  Card,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Badge,
  ActionIcon,
  Tooltip,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { IconEdit, IconTrash } from "@tabler/icons-react"
import { useMemo } from "react"

import type {
  AxesFeatureLabel,
  AxesFeatureLabelsMetadata,
} from "@costory/types/endpoints/labelsExplorer"
import {
  LabelsExplorerResponses,
  getTransformationType,
} from "@costory/types/endpoints/labelsExplorer"

import { StepType } from "@costory/front/pages/LabelsExplorer/components/stepTypes"

interface ResultPreviewPanelProps {
  initialLabels: LabelsExplorerResponses.LabelsStatsFullBilling[]
  axesFeatureLabels: AxesFeatureLabelsMetadata
  onDeleteAxesFeatureLabel?: (axesFeatureLabel: AxesFeatureLabel) => void
  form: UseFormReturnType<LabelsExplorerResponses.AxesFeatureLabelForm>
  setCompletedSteps: (
    completedSteps: Record<StepType, AxesFeatureLabel | null>,
  ) => void
  setOpenSteps: (openSteps: Record<StepType, boolean>) => void
  setEditingLabel: (editingLabel: AxesFeatureLabel | null) => void
}

interface FinalLabel {
  name: string
  type: "Merged" | "Transformed" | "Ingested"
  sources: string
  operations: string
  badges: string[]
  axesFeatureLabel: AxesFeatureLabel | null
}

export function ResultPreviewPanel({
  initialLabels,
  axesFeatureLabels,
  onDeleteAxesFeatureLabel,
  form,
  setCompletedSteps,
  setOpenSteps,
  setEditingLabel,
}: ResultPreviewPanelProps) {
  const finalLabels: FinalLabel[] = useMemo(() => {
    try {
      const labels: FinalLabel[] = []
      const consumedSourceKeys = new Set<string>()

      // Process all AxesFeatureLabels
      axesFeatureLabels.forEach((axesFeatureLabel) => {
        try {
          const transformationType = getTransformationType(axesFeatureLabel)
          const sourceNames = axesFeatureLabel.columnsMerged.map(
            (col) => col.name,
          )
          const additionalOps: string[] = []

          // Check if this has a custom name (different from default concatenated name for merges)
          if (transformationType === "merge") {
            const defaultName = sourceNames.join(" + ")
            const hasCustomName = axesFeatureLabel.name !== defaultName
            if (hasCustomName) {
              additionalOps.push("RENAMED")
            }
            if (axesFeatureLabel.maps.length > 0) {
              additionalOps.push("MAPPED")
            }
          }

          // Determine badges based on transformation type
          const badges: string[] = []
          if (transformationType === "merge") {
            badges.push("MERGED")
          } else if (transformationType === "ingest") {
            badges.push("INGESTED")
          } else if (transformationType === "rename") {
            badges.push("RENAMED")
          } else if (transformationType === "map") {
            const defaultName = sourceNames.join(" + ")
            const hasCustomName = axesFeatureLabel.name !== defaultName
            if (hasCustomName) {
              additionalOps.push("RENAMED")
            }
            badges.push("MAPPED")
          }

          badges.push(...additionalOps)

          // Create operations description
          let operations = ""
          if (transformationType === "merge") {
            operations = `Merged ${sourceNames.length} source labels${
              additionalOps.length ? `, ${additionalOps.join(", ")}` : ""
            }`
          } else if (transformationType === "ingest") {
            operations = "Ingested label"
          } else if (transformationType === "rename") {
            operations = "Renamed label"
          } else if (transformationType === "map") {
            operations = "Mapped values"
          }

          labels.push({
            name: axesFeatureLabel.name,
            type:
              transformationType === "merge"
                ? "Merged"
                : transformationType === "ingest"
                  ? "Ingested"
                  : "Transformed",
            sources: sourceNames.join(", "),
            operations,
            badges,
            axesFeatureLabel,
          })

          // Mark source keys as consumed
          sourceNames.forEach((key) => consumedSourceKeys.add(key))
        } catch (error) {
          console.error("Error processing axes feature label:", error)
        }
      })

      // Add unchanged labels
      initialLabels.forEach((label) => {
        if (!consumedSourceKeys.has(label.key)) {
          labels.push({
            name: label.key,
            type: "Transformed", // Use a default type since we don't need to distinguish
            sources: label.key,
            operations: "No transformation applied",
            badges: [],
            axesFeatureLabel: null,
          })
        }
      })

      // Filter to only show transformed labels (not unchanged ones)
      const transformedLabels = labels.filter((label) => {
        return label.axesFeatureLabel !== null // Only show labels that have been transformed
      })

      // Sort: latest transformed labels first, then alphabetically
      return transformedLabels.sort((a, b) => {
        if (a.axesFeatureLabel && b.axesFeatureLabel) {
          const aIndex = axesFeatureLabels.findIndex(
            (item) =>
              item.name === a.axesFeatureLabel?.name &&
              JSON.stringify(item.columnsMerged) ===
                JSON.stringify(a.axesFeatureLabel?.columnsMerged),
          )
          const bIndex = axesFeatureLabels.findIndex(
            (item) =>
              item.name === b.axesFeatureLabel?.name &&
              JSON.stringify(item.columnsMerged) ===
                JSON.stringify(b.axesFeatureLabel?.columnsMerged),
          )
          return bIndex - aIndex // Latest first (reverse order)
        }
        return a.name.localeCompare(b.name)
      })
    } catch (error) {
      console.error("Error generating final labels:", error)
      return []
    }
  }, [initialLabels, axesFeatureLabels])

  const handleEdit = (label: FinalLabel) => {
    setEditingLabel(label.axesFeatureLabel)

    form.setFieldValue(
      "columnsMerged",
      label.axesFeatureLabel?.columnsMerged || [],
    )
    form.setFieldValue("maps", label.axesFeatureLabel?.maps || [])
    form.setFieldValue("name", label.name)

    setCompletedSteps({
      [StepType.MERGE]:
        (label.axesFeatureLabel?.columnsMerged?.length ?? 0) > 1
          ? {
              name: label.name,
              columnsMerged: label.axesFeatureLabel?.columnsMerged || [],
              maps: [],
            }
          : null,
      [StepType.NAME]: {
        name: label.name,
        columnsMerged: label.axesFeatureLabel?.columnsMerged || [],
        maps: label.axesFeatureLabel?.maps || [],
      },
      [StepType.MAP]: {
        name: label.name,
        columnsMerged: label.axesFeatureLabel?.columnsMerged || [],
        maps: label.axesFeatureLabel?.maps || [],
      },
    })

    setOpenSteps({
      [StepType.MERGE]:
        (label.axesFeatureLabel?.columnsMerged?.length ?? 0) > 1,
      [StepType.NAME]: false,
      [StepType.MAP]: false,
    })
  }

  return (
    <Paper
      p="md"
      h="calc(100vh - 160px)"
      withBorder
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack gap="md" style={{ height: "100%" }}>
        <Title order={4} style={{ flexShrink: 0 }}>
          Result Preview
        </Title>

        <Box
          flex={1}
          style={{
            overflowY: "auto",
            minHeight: 0,
            maxHeight: "calc(100vh - 220px)",
          }}
        >
          <Stack gap="xs">
            {finalLabels.map((label) => (
              <Card key={label.name} withBorder p="sm" radius="sm">
                <Group
                  justify="space-between"
                  align="flex-start"
                  style={{ minHeight: "fit-content" }}
                >
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" mb="xs">
                      <Tooltip label={label.name}>
                        <Text size="sm" fw={500} truncate>
                          {label.name}
                        </Text>
                      </Tooltip>
                      {label.badges.map((badge) => (
                        <Badge
                          key={badge}
                          variant="light"
                          color={
                            badge === "MERGED"
                              ? "blue"
                              : badge === "RENAMED"
                                ? "orange"
                                : badge === "INGESTED"
                                  ? "green"
                                  : "gray"
                          }
                          size="xs"
                        >
                          {badge}
                        </Badge>
                      ))}
                    </Group>
                    <Text size="xs" c="dimmed" mb="xs">
                      {label.operations}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Sources: {label.sources}
                    </Text>
                  </Box>
                  <Group
                    gap="xs"
                    style={{ flexShrink: 0, alignSelf: "flex-start" }}
                  >
                    {label.axesFeatureLabel && (
                      <>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          onClick={() => handleEdit(label)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() =>
                            onDeleteAxesFeatureLabel?.(label.axesFeatureLabel!)
                          }
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </>
                    )}
                  </Group>
                </Group>
              </Card>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  )
}
